import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from '../components/Sidebar/Sidebar'
import MobileHeader from '../components/Sidebar/MobileHeader'
import MobileNav from '../components/Sidebar/MobileNav'
import ActionHUD from '../components/ActionHUD'
import { useAuth } from '../contexts/AuthContext'
import { usePlayerMeta } from '../hooks/usePlayerMeta'
import { useGlobalLevelUp } from '../hooks/useGlobalLevelUp'
import { useSkillXPGain } from '../hooks/useSkillXPGain'
import { useCraftComplete } from '../hooks/useCraftComplete'
import { useAchievementUnlock } from '../hooks/useAchievementUnlock'
import { useMarketNotifications } from '../hooks/useMarketNotifications'
import { useCatchUpDigest } from '../hooks/useCatchUpDigest'
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat'
import CatchUpDigestModal from '../components/CatchUp/CatchUpDigestModal'
import api from '../services/api'
import { useToast } from '../contexts/ToastContext'
import { clearWelcomeDismissal } from '../contexts/TutorialContext'
import ConfirmationModal from '../components/ui/ConfirmationModal'
import PushEnablePrompt from '../components/common/PushEnablePrompt'

export default function AuthenticatedLayout() {
    const { currentUser, demoModeEnabled, logout, isAuthenticated } = useAuth()
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
    const [isResetModalOpen, setIsResetModalOpen] = useState(false)
    const toast = useToast()
    const playerMeta = usePlayerMeta()
    const catchUpDigest = useCatchUpDigest()

    // Listeners
    useGlobalLevelUp(isAuthenticated)
    useSkillXPGain(isAuthenticated)
    useCraftComplete(isAuthenticated)
    useAchievementUnlock(isAuthenticated)
    useMarketNotifications(isAuthenticated)
    usePresenceHeartbeat(isAuthenticated)

    const handleResetCharacterClick = () => {
        setIsResetModalOpen(true)
    }

    const handleConfirmReset = async () => {
        try {
            await api.post('/player/reset')
            // Reset re-presents the tutorial: clear any "ignored welcome" marker
            clearWelcomeDismissal(currentUser?.id)
            toast.success('Character reset successfully!')
            window.location.reload()
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'Unknown error'
            console.error('Failed to reset character:', {
                message: errorMessage,
                status: error.response?.status,
                error: error
            })
            toast.error(`Failed to reset character: ${errorMessage}`)
        }
    }

    return (
        <div
            className="flex h-screen overflow-hidden bg-bg-darkest"
            style={{ '--sidebar-w': isSidebarCollapsed ? '72px' : '264px' }}
        >
            <Sidebar
                currentUser={currentUser}
                demoModeEnabled={demoModeEnabled}
                onResetCharacter={handleResetCharacterClick}
                onLogout={logout}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                playerMeta={playerMeta}
            />

            <ConfirmationModal
                open={isResetModalOpen}
                onOpenChange={setIsResetModalOpen}
                title="Reset Character?"
                description={`Are you sure you want to reset your character?

This will:
- Remove all items from inventory
- Delete all scavenging history
- Reset all metrics (health, stamina, hunger, currency, shelter level)
- Reset all crafting stations

This action cannot be undone!`}
                onConfirm={handleConfirmReset}
                confirmText="Reset Character"
                variant="danger"
            />

            <div className="flex-1 flex flex-col min-w-0 pl-[var(--sidebar-w)] max-lg:pl-0 transition-[padding] duration-200 ease-out">
                <MobileHeader />

                <main className="flex-1 overflow-y-auto px-4 pb-24 lg:pb-4 sm:px-6 custom-scrollbar relative scroll-smooth">
                    <div className="max-w-[1600px] mx-auto w-full">
                        <ActionHUD />
                        <div className="mt-4">
                            {/* Helper wrapper to handle route transitions if needed, or just Outlet */}
                            <Outlet />
                        </div>
                    </div>
                </main>

                <MobileNav
                    currentUser={currentUser}
                    demoModeEnabled={demoModeEnabled}
                    onResetCharacter={handleResetCharacterClick}
                    onLogout={logout}
                    playerMeta={playerMeta}
                />
            </div>

            <PushEnablePrompt />

            <CatchUpDigestModal
                digest={catchUpDigest.digest}
                isOpen={catchUpDigest.isOpen}
                onDismiss={catchUpDigest.dismiss}
            />
        </div>
    )
}
